import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  Autorenew,
  CheckCircleOutline,
  CloudUpload,
  DoneAll,
  ErrorOutline,
  GetApp,
  InfoOutlined,
  PlayArrow,
  Replay,
  WarningOutlined,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import SchainAuditDetail from "./SchainAuditDetail";
import SchainAuditResults from "./SchainAuditResults";
import {
  MAPPING_FIELDS,
  buildNormalizedRows,
  countSeverities,
  exportResultsCsv,
  exportResultsJson,
  filterResults,
  guessMapping,
  parseInput,
} from "./auditUtils";

const HEALTH_ENDPOINT = "/api/schain-audit-health.php";
const VALIDATE_ENDPOINT = "/api/schain-audit-validate.php";
const VALIDATION_BATCH_SIZE = 10;
const VALIDATION_TIMEOUT_MS = 7000;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gap: theme.spacing(2),
  },
  hero: {
    padding: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.secondary.main}10 0%, #ffffff 52%, ${theme.palette.info.main}08 100%)`,
  },
  heroTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  heroIntro: {
    maxWidth: 760,
  },
  heroMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  heroActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2.5),
  },
  bannerStack: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  progressCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  sectionCard: {
    padding: theme.spacing(2.5),
    height: "100%",
  },
  sectionHeader: {
    display: "grid",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },
  datasetMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  fieldGrid: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  mappingSelect: {
    minWidth: 0,
  },
  readinessGrid: {
    display: "grid",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  readinessCard: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  monoInput: {
    "& textarea": {
      fontFamily: '"Cascadia Code", Consolas, monospace',
      fontSize: 12,
      lineHeight: 1.5,
    },
  },
  helperText: {
    color: theme.palette.text.secondary,
  },
  subtleDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  actionSpacer: {
    flex: "1 1 auto",
  },
  compactButton: {
    whiteSpace: "nowrap",
  },
}));

function toneStyles(theme, tone) {
  if (tone === "success") {
    return {
      backgroundColor: `${theme.palette.success.main}12`,
      borderColor: `${theme.palette.success.main}33`,
      color: theme.palette.success.main,
    };
  }

  if (tone === "warning") {
    return {
      backgroundColor: `${theme.palette.warning.main}12`,
      borderColor: `${theme.palette.warning.main}44`,
      color: theme.palette.warning.main,
    };
  }

  if (tone === "error") {
    return {
      backgroundColor: `${theme.palette.error.main}10`,
      borderColor: `${theme.palette.error.main}33`,
      color: theme.palette.error.main,
    };
  }

  return {
    backgroundColor: `${theme.palette.info.main}10`,
    borderColor: `${theme.palette.info.main}33`,
    color: theme.palette.info.main,
  };
}

function mergeMappings(headers, previousMapping, guessedMapping) {
  return MAPPING_FIELDS.reduce((nextMapping, field) => {
    const previousHeader = previousMapping[field.key];
    if (previousHeader && headers.includes(previousHeader)) {
      nextMapping[field.key] = previousHeader;
      return nextMapping;
    }

    if (guessedMapping[field.key]) {
      nextMapping[field.key] = guessedMapping[field.key];
    }

    return nextMapping;
  }, {});
}

function formatSourceType(sourceType) {
  if (sourceType === "json-array" || sourceType === "json-object") {
    return "JSON";
  }

  if (sourceType === "tsv") {
    return "TSV";
  }

  if (sourceType === "line-list") {
    return "Domain list";
  }

  return "Delimited text";
}

function buildResponseError(label, response, payload, rawText) {
  if (payload && payload.error) {
    return `${label} failed: ${String(payload.error)}`;
  }

  const snippet = rawText ? rawText.replace(/\s+/g, " ").slice(0, 220) : "";
  if (snippet) {
    return `${label} failed with ${response.status}. Response snippet: ${snippet}`;
  }

  return `${label} failed with ${response.status}.`;
}

async function parseJsonResponse(response, label) {
  const rawText = await response.text();
  let payload = null;

  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch (error) {
      const snippet = rawText.replace(/\s+/g, " ").slice(0, 220);
      throw new Error(`${label} returned a non-JSON response.${snippet ? ` Response snippet: ${snippet}` : ""}`);
    }
  }

  if (!response.ok) {
    throw new Error(buildResponseError(label, response, payload, rawText));
  }

  if (!payload || typeof payload !== "object") {
    throw new Error(`${label} returned an empty response.`);
  }

  return payload;
}

function StatCard({ caption, helper, value }) {
  return (
    <Paper variant="outlined" style={{ height: "100%", padding: 18 }}>
      <Typography variant="overline" color="textSecondary">
        {caption}
      </Typography>
      <Typography variant="h4" style={{ marginTop: 8, marginBottom: 6, fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {helper}
      </Typography>
    </Paper>
  );
}

function FeedbackBanner({ tone, title, message }) {
  const palette = {
    success: {
      backgroundColor: "#eefbf5",
      borderColor: "#98e3c4",
      color: "#129166",
    },
    warning: {
      backgroundColor: "#fff8ea",
      borderColor: "#f0cc89",
      color: "#b7791f",
    },
    error: {
      backgroundColor: "#fff1f2",
      borderColor: "#f3b3bd",
      color: "#c53030",
    },
    info: {
      backgroundColor: "#f3eafe",
      borderColor: "#ccb7f6",
      color: "#6d28d9",
    },
  };

  const style = palette[tone] || palette.info;
  const icon =
    tone === "success"
      ? <CheckCircleOutline fontSize="small" style={{ marginTop: 2, color: style.color }} />
      : tone === "warning"
        ? <WarningOutlined fontSize="small" style={{ marginTop: 2, color: style.color }} />
        : tone === "error"
          ? <ErrorOutline fontSize="small" style={{ marginTop: 2, color: style.color }} />
          : <InfoOutlined fontSize="small" style={{ marginTop: 2, color: style.color }} />;

  return (
    <Paper
      variant="outlined"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: 14,
        backgroundColor: style.backgroundColor,
        borderColor: style.borderColor,
      }}
    >
      {icon}
      <Box>
        <Typography variant="subtitle2" style={{ color: style.color }}>
          {title}
        </Typography>
        <Typography variant="body2" style={{ color: style.color, marginTop: 2 }}>
          {message}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function SchainAudit() {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [rawInput, setRawInput] = useState("");
  const [delimiter, setDelimiter] = useState("auto");
  const [parsedInput, setParsedInput] = useState(null);
  const [mapping, setMapping] = useState({});
  const [isInputDirty, setIsInputDirty] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resultFilter, setResultFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [validationStatusText, setValidationStatusText] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [health, setHealth] = useState({
    loading: true,
    ok: false,
    cacheFresh: false,
    message: "Checking Google cache status...",
  });

  const preparedRows = useMemo(() => buildNormalizedRows(parsedInput, mapping), [parsedInput, mapping]);
  const filteredResults = useMemo(
    () => filterResults(results, resultFilter, searchQuery),
    [results, resultFilter, searchQuery],
  );
  const selectedResult = results[selectedIndex] || null;
  const summary = useMemo(
    () => countSeverities(results.flatMap((result) => result.issues || [])),
    [results],
  );
  const healthyChains = useMemo(
    () => results.filter((result) => result.supplyChain && result.supplyChain.status === "ok").length,
    [results],
  );
  const mappedFieldCount = useMemo(
    () => MAPPING_FIELDS.filter((field) => Boolean(mapping[field.key])).length,
    [mapping],
  );
  const readinessChecks = useMemo(() => {
    const inventoryReady = Boolean(
      mapping.publisherDomain || mapping.appDomain || mapping.appBundle || mapping.sellerDomain,
    );
    const nodesReady = Boolean(mapping.expectedSellerId && mapping.expectedAdSystem);
    const terminalReady = Boolean(mapping.networkId || mapping.publisherId);

    return [
      {
        label: "Inventory origin",
        status: inventoryReady ? "success" : "warning",
        description: inventoryReady
          ? "A domain or bundle field is mapped for the inventory source."
          : "Map publisher domain, app domain, bundle, or inventory partner domain.",
      },
      {
        label: "Schain nodes",
        status: nodesReady ? "success" : "warning",
        description: nodesReady
          ? "Seller IDs and ad systems are ready for live validation."
          : "Map both Expected Seller ID(s) and Expected Ad System(s).",
      },
      {
        label: "Terminal enrichment",
        status: terminalReady ? "info" : "warning",
        description: terminalReady
          ? "Network or publisher IDs are available for Equativ terminal context."
          : "Optional, but mapping Network ID or Publisher ID improves terminal context.",
      },
    ];
  }, [mapping]);

  useEffect(() => {
    if (selectedIndex >= results.length && results.length > 0) {
      setSelectedIndex(0);
    }
  }, [results, selectedIndex]);

  useEffect(() => {
    let isMounted = true;

    async function warmHealth() {
      try {
        const response = await fetch(`${HEALTH_ENDPOINT}?warm=1`);
        const payload = await parseJsonResponse(response, "Health endpoint");
        const cacheFresh = Boolean(payload.googleCache && payload.googleCache.fresh);

        if (!isMounted) {
          return;
        }

        setHealth({
          loading: false,
          ok: Boolean(payload.ok),
          cacheFresh,
          message: cacheFresh ? "Google cache ready" : "Google cache responded but is still warming",
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setHealth({
          loading: false,
          ok: false,
          cacheFresh: false,
          message: error.message || "Health endpoint is unavailable.",
        });
      }
    }

    warmHealth();

    return () => {
      isMounted = false;
    };
  }, []);

  function clearValidationOutputs() {
    setResults([]);
    setSelectedIndex(0);
    setResultFilter("all");
    setSearchQuery("");
    setValidationProgress(0);
    setValidationStatusText("");
  }

  function setBanner(tone, title, message) {
    setFeedback({ tone, title, message });
  }

  function applyParsedInput(sourceText, delimiterValue) {
    const nextParsed = parseInput(sourceText, delimiterValue);
    const guessedMapping = guessMapping(nextParsed.headers);
    const nextMapping = mergeMappings(nextParsed.headers, mapping, guessedMapping);
    const readyRows = buildNormalizedRows(nextParsed, nextMapping);

    setParsedInput(nextParsed);
    setMapping(nextMapping);
    setIsInputDirty(false);
    clearValidationOutputs();
    setBanner(
      "info",
      "Rows prepared",
      `Detected ${formatSourceType(nextParsed.sourceType)} with ${nextParsed.headers.length} column${
        nextParsed.headers.length === 1 ? "" : "s"
      } and ${readyRows.length} row${readyRows.length === 1 ? "" : "s"} ready for validation.`,
    );
  }

  function handlePrepareRows() {
    try {
      applyParsedInput(rawInput, delimiter);
    } catch (error) {
      setParsedInput(null);
      setMapping({});
      clearValidationOutputs();
      setBanner("error", "Dataset could not be prepared", error.message || "The input could not be parsed.");
    }
  }

  function handleRawChange(event) {
    setRawInput(event.target.value);
    setIsInputDirty(Boolean(parsedInput));
    clearValidationOutputs();
    setFeedback(null);
  }

  function handleDelimiterChange(event) {
    setDelimiter(event.target.value);
    if (parsedInput) {
      setIsInputDirty(true);
      clearValidationOutputs();
    }
    setFeedback(null);
  }

  function handleMappingChange(fieldKey, nextHeader) {
    setMapping((previous) => ({
      ...previous,
      [fieldKey]: nextHeader || "",
    }));
    clearValidationOutputs();
    setFeedback(null);
  }

  function handleReset() {
    setRawInput("");
    setDelimiter("auto");
    setParsedInput(null);
    setMapping({});
    setIsInputDirty(false);
    clearValidationOutputs();
    setFeedback(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function refreshHealth(warm) {
    setHealth((previous) => ({
      ...previous,
      loading: true,
      message: warm ? "Refreshing Google cache..." : previous.message,
    }));

    try {
      const response = await fetch(`${HEALTH_ENDPOINT}${warm ? "?warm=1" : ""}`);
      const payload = await parseJsonResponse(response, "Health endpoint");
      const cacheFresh = Boolean(payload.googleCache && payload.googleCache.fresh);

      setHealth({
        loading: false,
        ok: Boolean(payload.ok),
        cacheFresh,
        message: cacheFresh ? "Google cache ready" : "Google cache responded but is still warming",
      });
    } catch (error) {
      setHealth({
        loading: false,
        ok: false,
        cacheFresh: false,
        message: error.message || "Health endpoint is unavailable.",
      });
    }
  }

  async function handleValidate() {
    if (!parsedInput) {
      setBanner("warning", "Prepare rows first", "Paste a dataset and run Prepare Rows before validating.");
      return;
    }

    if (isInputDirty) {
      setBanner(
        "warning",
        "Dataset changed",
        "The pasted input was edited after the last preparation step. Run Prepare Rows again so the mapping stays in sync.",
      );
      return;
    }

    if (preparedRows.length === 0) {
      setBanner(
        "warning",
        "No usable rows",
        "The current mapping does not produce any rows with inventory origin or schain node data.",
      );
      return;
    }

    clearValidationOutputs();
    setIsValidating(true);
    setValidationProgress(2);
    setValidationStatusText("Warming shared caches and starting validation...");
    setFeedback(null);

    try {
      await refreshHealth(true);

      const collectedResults = [];
      const totalBatches = Math.ceil(preparedRows.length / VALIDATION_BATCH_SIZE);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
        const start = batchIndex * VALIDATION_BATCH_SIZE;
        const batchRows = preparedRows.slice(start, start + VALIDATION_BATCH_SIZE);

        setValidationStatusText(`Processing batch ${batchIndex + 1} of ${totalBatches}...`);

        const response = await fetch(VALIDATE_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            rows: batchRows,
            timeoutMs: VALIDATION_TIMEOUT_MS,
          }),
        });

        const payload = await parseJsonResponse(response, "Validation endpoint");
        if (!Array.isArray(payload.results)) {
          throw new Error("Validation endpoint returned JSON without a results array.");
        }

        payload.results.forEach((result) => {
          collectedResults.push(result);
        });

        setResults(collectedResults.slice());
        setValidationProgress(Math.round((collectedResults.length / preparedRows.length) * 100));
      }

      setValidationProgress(100);
      setValidationStatusText(`Validation complete. Processed ${collectedResults.length} row${collectedResults.length === 1 ? "" : "s"}.`);
      setSelectedIndex(collectedResults.length > 0 ? 0 : 0);
      setBanner(
        "success",
        "Validation complete",
        `Processed ${collectedResults.length} prepared row${collectedResults.length === 1 ? "" : "s"} across ${totalBatches} live validation batch${totalBatches === 1 ? "" : "es"}.`,
      );
    } catch (error) {
      setValidationStatusText("Validation stopped before completion.");
      setBanner(
        "error",
        "Validation failed",
        error.message || "The validation endpoint could not complete this request.",
      );
    } finally {
      setIsValidating(false);
    }
  }

  async function handleUpload(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const fileText = await file.text();
      setRawInput(fileText);
      applyParsedInput(fileText, delimiter);
    } catch (error) {
      setBanner("error", "File could not be read", error.message || "The uploaded file could not be opened.");
    }
  }

  return (
    <Box className={classes.root}>
      <Paper variant="outlined" className={classes.hero}>
        <Box className={classes.heroTop}>
          <Box className={classes.heroIntro}>
            <Typography variant="overline" color="textSecondary">
              Native Audit
            </Typography>
            <Typography variant="h5" style={{ marginTop: 6, fontWeight: 700 }}>
              Supply Chain Audit
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 10, maxWidth: 720 }}>
              Prepare raw schain exports, confirm the inferred mapping, run live sellers.json validation, and review each
              row in a full-width evidence workspace that is easier to scan than the previous dashboard layout.
            </Typography>
          </Box>

          <Box className={classes.heroMeta}>
            <Chip
              label={health.loading ? "Google cache checking" : health.message}
              style={{
                backgroundColor: health.loading
                  ? "#f3f4f6"
                  : health.cacheFresh
                    ? "#eefbf5"
                    : health.ok
                      ? "#fff7e8"
                      : "#fff1f2",
                color: health.loading
                  ? "#4b5563"
                  : health.cacheFresh
                    ? "#129166"
                    : health.ok
                      ? "#b7791f"
                      : "#c53030",
                fontWeight: 700,
              }}
            />
            <Chip label={`${preparedRows.length} prepared rows`} variant="outlined" />
            <Chip label={`${results.length} validated rows`} variant="outlined" />
            <Button
              size="small"
              variant="outlined"
              startIcon={<Autorenew fontSize="small" />}
              className={classes.compactButton}
              onClick={() => refreshHealth(true)}
              disabled={health.loading || isValidating}
            >
              Warm cache
            </Button>
          </Box>
        </Box>

        <Box className={classes.heroActions}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".csv,.tsv,.txt,.json"
            onChange={handleUpload}
          />
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CloudUpload />}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            Upload dataset
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DoneAll />}
            onClick={handlePrepareRows}
            disabled={!rawInput.trim() || isValidating}
          >
            Prepare rows
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={handleValidate}
            disabled={isValidating || !parsedInput || preparedRows.length === 0 || isInputDirty}
          >
            Run validation
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => exportResultsJson(results)}
            disabled={results.length === 0}
          >
            Export JSON
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => exportResultsCsv(results)}
            disabled={results.length === 0}
          >
            Export CSV
          </Button>
          <Box className={classes.actionSpacer} />
          <Button variant="text" startIcon={<Replay />} onClick={handleReset} disabled={isValidating}>
            Reset audit
          </Button>
        </Box>

        <Box className={classes.bannerStack}>
          {isInputDirty ? (
            <FeedbackBanner
              tone="warning"
              title="Dataset changed"
              message="The input was edited after the last preparation step. Run Prepare Rows again before validating."
            />
          ) : null}

          {feedback ? (
            <FeedbackBanner tone={feedback.tone} title={feedback.title} message={feedback.message} />
          ) : null}
        </Box>

        {(isValidating || validationProgress > 0 || validationStatusText) ? (
          <Paper variant="outlined" className={classes.progressCard}>
            <Box display="flex" justifyContent="space-between" alignItems="center" style={{ gap: 12 }}>
              <Typography variant="subtitle2">
                {validationStatusText || "Validation progress"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {validationProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={validationProgress}
              style={{ marginTop: 12, height: 8, borderRadius: 999 }}
            />
          </Paper>
        ) : null}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            caption="Prepared rows"
            value={preparedRows.length}
            helper={
              parsedInput
                ? `${parsedInput.rows.length} parsed input row${parsedInput.rows.length === 1 ? "" : "s"} ready for mapping.`
                : "Paste or upload a dataset to prepare rows."
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            caption="Errors"
            value={summary.error || 0}
            helper="Blocking seller or chain issues found in the current validation run."
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            caption="Warnings"
            value={summary.warning || 0}
            helper="Non-blocking mismatches, brand-domain fallbacks, or incomplete input."
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            caption="Healthy chains"
            value={healthyChains}
            helper={`${results.length - healthyChains > 0 ? results.length - healthyChains : 0} row${results.length - healthyChains === 1 ? "" : "s"} still need attention.`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography variant="overline" color="textSecondary">
                Step 1
              </Typography>
              <Typography variant="h6">Prepare raw audit rows</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Paste CSV, TSV, delimited text, JSON row arrays, or plain domain lists. Once the input is parsed, the
                audit keeps one normalized row shape for validation.
              </Typography>
            </Box>

            {parsedInput ? (
              <Box className={classes.datasetMeta}>
                <Chip label={formatSourceType(parsedInput.sourceType)} size="small" />
                <Chip label={`${parsedInput.headers.length} column${parsedInput.headers.length === 1 ? "" : "s"}`} size="small" />
                <Chip label={`${mappedFieldCount}/${MAPPING_FIELDS.length} mapped`} size="small" />
                <Chip label={`${preparedRows.length} ready row${preparedRows.length === 1 ? "" : "s"}`} size="small" />
              </Box>
            ) : null}

            <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 16, marginBottom: 16 }}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
                <InputLabel id="schain-audit-delimiter-label">Delimiter</InputLabel>
                <Select
                  labelId="schain-audit-delimiter-label"
                  value={delimiter}
                  onChange={handleDelimiterChange}
                  label="Delimiter"
                >
                  <MenuItem value="auto">Auto detect</MenuItem>
                  <MenuItem value=",">Comma</MenuItem>
                  <MenuItem value="\t">Tab</MenuItem>
                  <MenuItem value=";">Semicolon</MenuItem>
                  <MenuItem value="|">Pipe</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" className={classes.helperText}>
                Header detection and alias mapping are automatic, but you can override any field in the next panel.
              </Typography>
            </Box>

            <TextField
              multiline
              fullWidth
              rows={16}
              variant="outlined"
              placeholder="Paste raw schain rows, JSON arrays, or a plain domain list here"
              value={rawInput}
              onChange={handleRawChange}
              className={classes.monoInput}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" className={classes.sectionCard}>
            <Box className={classes.sectionHeader}>
              <Typography variant="overline" color="textSecondary">
                Step 1
              </Typography>
              <Typography variant="h6">Confirm the inferred mapping</Typography>
              <Typography variant="body2" className={classes.helperText}>
                Auto-mapping uses common schain aliases. Review the key fields before validation so the prepared rows
                reflect the dataset you actually pasted.
              </Typography>
            </Box>

            <Box className={classes.readinessGrid}>
              {readinessChecks.map((check) => (
                <Box
                  key={check.label}
                  className={classes.readinessCard}
                  style={toneStyles(
                    {
                      palette: {
                        success: { main: "#129166" },
                        warning: { main: "#b7791f" },
                        error: { main: "#c53030" },
                        info: { main: "#6d28d9" },
                        divider: "#d7dce1",
                      },
                    },
                    check.status,
                  )}
                >
                  <Typography variant="subtitle2">{check.label}</Typography>
                  <Typography variant="body2" style={{ marginTop: 4 }}>
                    {check.description}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider className={classes.subtleDivider} />

            {parsedInput ? (
              <Box className={classes.fieldGrid}>
                {MAPPING_FIELDS.map((field) => (
                  <FormControl key={field.key} variant="outlined" size="small" className={classes.mappingSelect}>
                    <InputLabel id={`mapping-${field.key}`}>{field.label}</InputLabel>
                    <Select
                      labelId={`mapping-${field.key}`}
                      value={mapping[field.key] || ""}
                      onChange={(event) => handleMappingChange(field.key, event.target.value)}
                      label={field.label}
                    >
                      <MenuItem value="">Not mapped</MenuItem>
                      {parsedInput.headers.map((header) => (
                        <MenuItem key={`${field.key}-${header}`} value={header}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" className={classes.helperText}>
                Prepare rows first to unlock the mapping editor.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography variant="overline" color="textSecondary">
            Step 2
          </Typography>
          <Typography variant="h6">Review the selected validation row</Typography>
          <Typography variant="body2" className={classes.helperText}>
            The selected row gets a full-width workspace with a vertical payment path, cleaner issue triage, and grouped
            evidence tabs instead of the old squeezed horizontal strip.
          </Typography>
        </Box>

        <SchainAuditDetail result={selectedResult} />
      </Paper>

      <Paper variant="outlined" className={classes.sectionCard}>
        <Box className={classes.sectionHeader}>
          <Typography variant="overline" color="textSecondary">
            Step 3
          </Typography>
          <Typography variant="h6">Explore validated rows</Typography>
          <Typography variant="body2" className={classes.helperText}>
            Filter by chain health, search seller IDs or domains, and move quickly between rows without losing the
            selected detail workspace above.
          </Typography>
        </Box>

        <SchainAuditResults
          filter={resultFilter}
          filteredResults={filteredResults}
          onFilterChange={setResultFilter}
          onSearchChange={setSearchQuery}
          onSelect={setSelectedIndex}
          results={results}
          searchQuery={searchQuery}
          selectedIndex={selectedIndex}
        />
      </Paper>
    </Box>
  );
}
